import ApiClient from '@/domain/core/utils/apiClient'
import client_core from '@/services/clients/client_core'
const client = new ApiClient(client_core)

const endpoints = {
  list: '/dashboard/api/v3/email-scheduler/{scope}/schedules/',
  detail: '/dashboard/api/v3/email-scheduler/{scope}/schedules/{scheduleId}/',
  forceRestart: '/dashboard/api/v3/email-scheduler/{scope}/schedules/{scheduleId}/force_restart/',
  debugInfo: '/dashboard/api/v3/email-scheduler/{scope}/schedules/{scheduleId}/debug_info/'
}

/**
 * Build scope path for API endpoints
 * @param {Object} scope - {type: 'global'|'contract-manager'|'event-owner'|'pica-service', id: number}
 * @returns {string} - Scope path for URL
 */
const buildScope = (scope) => {
  if (scope.type === 'global') {
    return 'global'
  } else if (scope.type === 'contract-manager') {
    return `contract-manager/${scope.id}`
  } else if (scope.type === 'event-owner') {
    return `event-owner/${scope.id}`
  } else if (scope.type === 'pica-service') {
    return `pica-service/${scope.id}`
  }
  throw new Error(`Invalid scope type: ${scope.type}`)
}

export default {
  /**
   * Fetch all email schedules for a given scope
   * @param {Object} scope
   * @returns {Promise<Array>}
   */
  async fetchEmailSchedules(scope) {
    const scopePath = buildScope(scope)
    const url = endpoints.list.replace('{scope}', scopePath)
    return await client.get(url)
  },

  /**
   * Fetch a single email schedule by ID
   * @param {Object} scope
   * @param {string} scheduleId
   * @returns {Promise<Object>}
   */
  async fetchEmailSchedule(scope, scheduleId) {
    const scopePath = buildScope(scope)
    const url = endpoints.detail
      .replace('{scope}', scopePath)
      .replace('{scheduleId}', scheduleId)
    return await client.get(url)
  },

  /**
   * Create a new email schedule
   * @param {Object} scope
   * @param {Object} data
   * @returns {Promise<Object>}
   */
  async createEmailSchedule(scope, data) {
    const scopePath = buildScope(scope)
    const url = endpoints.list.replace('{scope}', scopePath)
    return await client.post(url, data)
  },

  /**
   * Update an existing email schedule
   * @param {Object} scope
   * @param {string} scheduleId
   * @param {Object} data
   * @returns {Promise<Object>}
   */
  async updateEmailSchedule(scope, scheduleId, data) {
    const scopePath = buildScope(scope)
    const url = endpoints.detail
      .replace('{scope}', scopePath)
      .replace('{scheduleId}', scheduleId)
    return await client.put(url, data)
  },

  /**
   * Delete an email schedule
   * @param {Object} scope
   * @param {string} scheduleId
   * @returns {Promise<void>}
   */
  async deleteEmailSchedule(scope, scheduleId) {
    const scopePath = buildScope(scope)
    const url = endpoints.detail
      .replace('{scope}', scopePath)
      .replace('{scheduleId}', scheduleId)
    return await client.delete(url)
  },

  /**
   * Force restart an email schedule
   * @param {Object} scope
   * @param {string} scheduleId
   * @returns {Promise<Object>}
   */
  async forceRestart(scope, scheduleId) {
    const scopePath = buildScope(scope)
    const url = endpoints.forceRestart
      .replace('{scope}', scopePath)
      .replace('{scheduleId}', scheduleId)
    return await client.post(url)
  },

  /**
   * Get debug info for an email schedule
   * @param {Object} scope
   * @param {string} scheduleId
   * @returns {Promise<Object>}
   */
  async fetchDebugInfo(scope, scheduleId) {
    const scopePath = buildScope(scope)
    const url = endpoints.debugInfo
      .replace('{scope}', scopePath)
      .replace('{scheduleId}', scheduleId)
    return await client.get(url)
  }
}
